import {
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    chakra,
    Flex,
    IconButton,
    Text,
    Box,
    Checkbox,
} from '@chakra-ui/react';
import {
    useReactTable,
    flexRender,
    getCoreRowModel,
    ColumnDef,
    SortingState,
} from '@tanstack/react-table';
import {
    MdArrowUpward,
    MdArrowDownward,
    MdChevronLeft,
    MdChevronRight,
    MdFirstPage,
    MdLastPage,
} from 'react-icons/md';
import { useState, useEffect } from 'react';

export type DataTableProps<Data extends object> = {
    data: Data[];
    columns: ColumnDef<Data, any>[];
    total: number;
    page: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    onSortChange?: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    isLoading?: boolean;
    enableRowSelection?: boolean;
    onRowSelectionChange?: (selectedIds: number[]) => void;
};

export function DataTable<Data extends object & { id: number | string }>({
    data,
    columns,
    total,
    page,
    totalPages,
    onPageChange,
    onSortChange,
    sortBy,
    sortOrder,
    isLoading,
    enableRowSelection,
    onRowSelectionChange,
}: DataTableProps<Data>) {
    const [sorting, setSorting] = useState<SortingState>(
        sortBy ? [{ id: sortBy, desc: sortOrder === 'desc' }] : []
    );
    const [rowSelection, setRowSelection] = useState({});

    // Sync external sort props into internal table state
    useEffect(() => {
        if (sortBy) {
            setSorting([{ id: sortBy, desc: sortOrder === 'desc' }]);
        } else {
            setSorting([]);
        }
    }, [sortBy, sortOrder]);

    // Reset selection when data changes (e.g. after deletion or paging)
    // to prevent "ghost" selections of new items at the same indices.
    useEffect(() => {
        setRowSelection({});
    }, [data]);

    const table = useReactTable({
        data,
        columns,
        getRowId: (row) => String(row.id),
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: (updater) => {
            const nextSorting = typeof updater === 'function' ? updater(sorting) : updater;
            setSorting(nextSorting);
            if (nextSorting.length > 0 && onSortChange) {
                const sort = nextSorting[0];
                onSortChange(sort.id, sort.desc ? 'desc' : 'asc');
            }
        },
        onRowSelectionChange: (updater) => {
            const nextSelection = typeof updater === 'function' ? updater(rowSelection) : updater;
            setRowSelection(nextSelection);

            if (onRowSelectionChange) {
                const selectedIds = Object.keys(nextSelection)
                    .filter(key => nextSelection[key])
                    .map(key => Number(key)); // Since getRowId returns the ID, keys are the IDs
                onRowSelectionChange(selectedIds);
            }
        },
        state: {
            sorting,
            rowSelection,
        },
        enableRowSelection: enableRowSelection,
        manualSorting: true,
    });

    return (
        <Box borderWidth="1px" borderRadius="lg">
            <Box overflowX="auto" sx={{
                '&::-webkit-scrollbar': { height: '8px' },
                '&::-webkit-scrollbar-track': { bg: 'transparent' },
                '&::-webkit-scrollbar-thumb': { bg: 'gray.200', _dark: { bg: 'gray.700' }, borderRadius: 'full' }
            }}>
                <Table variant="simple" size="sm">
                    <Thead bg="gray.50" _dark={{ bg: 'gray.700' }}>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <Tr key={headerGroup.id}>
                                {enableRowSelection && (
                                    <Th w="40px" px={2}>
                                        <Checkbox
                                            isChecked={table.getIsAllPageRowsSelected()}
                                            isIndeterminate={table.getIsSomePageRowsSelected()}
                                            onChange={table.getToggleAllPageRowsSelectedHandler()}
                                            colorScheme="brand"
                                        />
                                    </Th>
                                )}
                                {headerGroup.headers.map((header) => {
                                    const meta: any = header.column.columnDef.meta;
                                    return (
                                        <Th
                                            key={header.id}
                                            onClick={header.column.getToggleSortingHandler()}
                                            isNumeric={meta?.isNumeric}
                                            cursor={header.column.getCanSort() ? 'pointer' : 'default'}
                                            userSelect="none"
                                            whiteSpace="nowrap"
                                        >
                                            <Flex align="center" gap={1}>
                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                                <chakra.span pl="4">
                                                    {header.column.getIsSorted() ? (
                                                        header.column.getIsSorted() === 'desc' ? (
                                                            <MdArrowDownward aria-label="sorted descending" />
                                                        ) : (
                                                            <MdArrowUpward aria-label="sorted ascending" />
                                                        )
                                                    ) : null}
                                                </chakra.span>
                                            </Flex>
                                        </Th>
                                    );
                                })}
                            </Tr>
                        ))}
                    </Thead>
                    <Tbody>
                        {table.getRowModel().rows.map((row) => (
                            <Tr
                                key={row.id}
                                _hover={{ bg: 'gray.50', _dark: { bg: 'gray.700' } }}
                                bg={row.getIsSelected() ? 'brand.50' : 'inherit'}
                                _dark={{
                                    bg: row.getIsSelected() ? 'whiteAlpha.100' : 'inherit'
                                }}
                            >
                                {enableRowSelection && (
                                    <Td w="40px" px={2}>
                                        <Checkbox
                                            isChecked={row.getIsSelected()}
                                            onChange={row.getToggleSelectedHandler()}
                                            colorScheme="brand"
                                        />
                                    </Td>
                                )}
                                {row.getVisibleCells().map((cell) => {
                                    const meta: any = cell.column.columnDef.meta;
                                    return (
                                        <Td key={cell.id} isNumeric={meta?.isNumeric} whiteSpace="nowrap">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </Td>
                                    );
                                })}
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </Box>

            {/* Pagination Controls */}
            <Flex justify="space-between" align="center" p={4} borderTopWidth="1px">
                <Text fontSize="sm" color="gray.500">
                    Page {page} of {totalPages} ({total} items)
                </Text>
                <Flex gap={2}>
                    <IconButton
                        aria-label="First Page"
                        icon={<MdFirstPage />}
                        onClick={() => onPageChange(1)}
                        isDisabled={page === 1 || isLoading}
                        size="sm"
                    />
                    <IconButton
                        aria-label="Previous Page"
                        icon={<MdChevronLeft />}
                        onClick={() => onPageChange(page - 1)}
                        isDisabled={page === 1 || isLoading}
                        size="sm"
                    />
                    <IconButton
                        aria-label="Next Page"
                        icon={<MdChevronRight />}
                        onClick={() => onPageChange(page + 1)}
                        isDisabled={page === totalPages || isLoading}
                        size="sm"
                    />
                    <IconButton
                        aria-label="Last Page"
                        icon={<MdLastPage />}
                        onClick={() => onPageChange(totalPages)}
                        isDisabled={page === totalPages || isLoading}
                        size="sm"
                    />
                </Flex>
            </Flex>
        </Box>
    );
}
